/**
 * @spec docs/specs/features/staking-ui-polish.md#quote-integration
 *
 * API client for phnm-swap-api with quote integration.
 * Uses centralized env config and implements backend quote fallback pattern.
 */
import { formatUnits } from 'ethers';
import { env } from './env';
import type { SwapRequest, SwapResponse, SwapQuote } from '../types/swap';

// Use centralized env config (removes incorrect 3009 fallback)
const SWAP_API_URL = env.swapApiUrl;

// Token addresses and decimals
const USDC_ADDRESS = env.contracts.usdcTokenAddress;
const USDC_DECIMALS = 6;
const PHNM_DECIMALS = 18;

/**
 * Calculate exchange rate for display purposes.
 * Normalizes token amounts to account for decimal differences (PHNM=18, USDC=6).
 *
 * Uses BigInt for all calculations, then formatUnits for safe conversion to display.
 * Follows project pattern from phnm-claim-api and phnm-staking-ui/src/lib/format.ts.
 * Integer division truncates (floors) - "truncate, never round up" for financial calculations.
 *
 * @param amountIn - Input amount in wei (string)
 * @param amountOut - Output amount in wei (string)
 * @param decimalsIn - Decimals of input token (e.g., 18 for PHNM, 6 for USDC)
 * @param decimalsOut - Decimals of output token
 * @returns Exchange rate as number for display (e.g., 0.245 USDC per PHNM)
 */
function calculateExchangeRate(
  amountIn: string,
  amountOut: string,
  decimalsIn: number,
  decimalsOut: number
): number {
  const inBigInt = BigInt(amountIn);
  const outBigInt = BigInt(amountOut);

  if (inBigInt === BigInt(0)) return 0;

  // Normalize both amounts to 18 decimals for consistent calculation
  const normalizedIn = inBigInt * BigInt(10 ** (18 - decimalsIn));
  const normalizedOut = outBigInt * BigInt(10 ** (18 - decimalsOut));

  // Scale up for precision before division (8 decimals sufficient for exchange rates)
  // BigInt division truncates toward zero (floors for positive numbers)
  const RATE_DECIMALS = 8;
  const scaledRate = (normalizedOut * BigInt(10 ** RATE_DECIMALS)) / normalizedIn;

  // Use formatUnits for safe BigInt-to-string conversion (project standard pattern)
  // Then parse to number for display
  return parseFloat(formatUnits(scaledRate, RATE_DECIMALS));
}

/**
 * Get token decimals from address
 */
function getTokenDecimals(tokenAddress: string): number {
  return tokenAddress.toLowerCase() === USDC_ADDRESS?.toLowerCase() ? USDC_DECIMALS : PHNM_DECIMALS;
}

/**
 * Fetches a swap quote from the backend.
 * Falls back to mock calculation if backend quote endpoint is unavailable.
 */
export async function getSwapQuote(
  tokenIn: string,
  tokenOut: string,
  amountIn: string,
  slippageBps: number = 50
): Promise<SwapQuote> {
  const decimalsIn = getTokenDecimals(tokenIn);
  const decimalsOut = getTokenDecimals(tokenOut);

  try {
    // Set up timeout (5 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    // Attempt to fetch from backend quote endpoint
    const response = await fetch(
      `${SWAP_API_URL}/quote?` +
        new URLSearchParams({
          tokenIn,
          tokenOut,
          amountIn,
        }),
      { signal: controller.signal }
    );

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();

      // Calculate amountOutMin from slippage (integer division truncates)
      const amountOutBigInt = BigInt(data.amountOut);
      const slippageMultiplier = BigInt(10000 - slippageBps);
      const amountOutMin = ((amountOutBigInt * slippageMultiplier) / BigInt(10000)).toString();

      return {
        amountIn: data.amountIn,
        amountOut: data.amountOut,
        amountOutMin,
        priceImpact: data.priceImpact,
        exchangeRate: calculateExchangeRate(data.amountIn, data.amountOut, decimalsIn, decimalsOut),
        path: data.route?.map((r: { from: string }) => r.from) || [tokenIn, tokenOut],
      };
    }

    // Fall through to mock if endpoint returns non-OK
    throw new Error('Quote endpoint unavailable');
  } catch {
    // Fallback to mock quote (temporary until backend implements /quote)
    return getMockQuote(tokenIn, tokenOut, amountIn, slippageBps, decimalsIn, decimalsOut);
  }
}

/**
 * Mock quote implementation - used as fallback when backend /quote is unavailable.
 * TODO: Remove once backend GET /quote endpoint is implemented.
 */
function getMockQuote(
  tokenIn: string,
  tokenOut: string,
  amountIn: string,
  slippageBps: number,
  decimalsIn: number,
  decimalsOut: number
): SwapQuote {
  const amountInBigInt = BigInt(amountIn);
  const isUsdcIn = tokenIn.toLowerCase() === USDC_ADDRESS?.toLowerCase();

  // Mock 1:1 decimal conversion (NOT accurate - for UI testing only)
  // Integer division truncates - follows project pattern
  const amountOut = isUsdcIn
    ? (amountInBigInt * BigInt(10 ** 12)).toString() // USDC (6) -> PHNM (18): multiply
    : (amountInBigInt / BigInt(10 ** 12)).toString(); // PHNM (18) -> USDC (6): divide

  const slippageMultiplier = BigInt(10000 - slippageBps);
  const amountOutMin = ((BigInt(amountOut) * slippageMultiplier) / BigInt(10000)).toString();

  return {
    amountIn,
    amountOut,
    amountOutMin,
    priceImpact: 0.1, // Mock value
    exchangeRate: calculateExchangeRate(amountIn, amountOut, decimalsIn, decimalsOut),
    path: [tokenIn, tokenOut],
  };
}

/**
 * Submit a gasless swap request
 */
export async function submitSwap(request: SwapRequest): Promise<SwapResponse> {
  const response = await fetch(`${SWAP_API_URL}/swap`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({
      error: 'Unknown error',
    }))) as { error?: string };
    throw new Error(errorData.error || response.statusText);
  }

  return response.json() as Promise<SwapResponse>;
}

/**
 * Get transaction status.
 * Tolerates 429 (rate limit) by returning a synthetic 'pending' response
 * so the caller can keep polling instead of throwing an error.
 */
export async function getSwapStatus(
  txId: string,
  signal?: AbortSignal
): Promise<SwapResponse | null> {
  const response = await fetch(`${SWAP_API_URL}/status/${txId}`, { signal });

  if (response.status === 404) {
    return null;
  }

  // Treat rate-limit as "still pending" so polling continues
  if (response.status === 429) {
    return { txId, status: 'pending' };
  }

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({
      error: 'Unknown error',
    }))) as { error?: string };
    throw new Error(errorData.error || response.statusText);
  }

  return response.json() as Promise<SwapResponse>;
}

/**
 * Check swap API health
 */
export async function checkSwapApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${SWAP_API_URL}/health`);
    if (!response.ok) return false;

    const data = (await response.json()) as { status?: string; swapEnabled?: boolean };
    return data.status === 'healthy' && data.swapEnabled === true;
  } catch {
    return false;
  }
}

/**
 * Check if an address is authorized for bridge/cashout operations.
 * Returns false if the endpoint is unreachable (fail-closed).
 */
export async function checkBridgeAllowlist(address: string): Promise<boolean> {
  try {
    const response = await fetch(`${SWAP_API_URL}/bridge/allowlist/${address}`);
    if (!response.ok) return false;

    const data = (await response.json()) as { allowed: boolean };
    return data.allowed;
  } catch {
    return false;
  }
}

// ── Flow tracking API (server-side Privy multi-step flow) ──

export interface SwapFlowResponse {
  id: string;
  userAddress: string;
  status: 'active' | 'completed' | 'failed' | 'expired';
  currentStep: 1 | 2 | 3;
  step1Status: string;
  step2Status: string;
  step3Status: string;
  phnmIn: string;
  usdcOut: string | null;
  usdtOut: string | null;
  swapTxHash: string | null;
  bridgeDepositTxHash: string | null;
  bridgeReleaseTxHash: string | null;
  transferTxHash: string | null;
  errorMessage: string | null;
  errorStep: number | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

/**
 * Create a new server-side flow for the Privy multi-step swap
 */
export async function createFlow(userAddress: string, phnmIn: string): Promise<SwapFlowResponse> {
  const response = await fetch(`${SWAP_API_URL}/flow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userAddress, phnmIn }),
  });

  if (!response.ok) {
    const err = (await response.json().catch(() => ({ error: 'Unknown error' }))) as {
      error?: string;
    };
    throw new Error(err.error || response.statusText);
  }

  return response.json() as Promise<SwapFlowResponse>;
}

/**
 * Get flow state by ID (also polls bridge-relayer for step 2 updates)
 */
export async function getFlowStatus(flowId: string): Promise<SwapFlowResponse | null> {
  const response = await fetch(`${SWAP_API_URL}/flow/${flowId}`);
  if (response.status === 404) return null;
  if (!response.ok) return null;
  return response.json() as Promise<SwapFlowResponse>;
}

/**
 * Update flow state (step status, tx hashes, amounts)
 */
export async function updateFlow(
  flowId: string,
  updates: Record<string, unknown>
): Promise<SwapFlowResponse | null> {
  const response = await fetch(`${SWAP_API_URL}/flow/${flowId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (response.status === 404) return null;
  if (!response.ok) return null;
  return response.json() as Promise<SwapFlowResponse>;
}

/**
 * Get active (incomplete) flows for a user address
 */
export async function getActiveFlows(address: string): Promise<SwapFlowResponse[]> {
  try {
    const response = await fetch(`${SWAP_API_URL}/flow/active/${address}`);
    if (!response.ok) return [];
    const data = (await response.json()) as { flows: SwapFlowResponse[] };
    return data.flows;
  } catch {
    return [];
  }
}

/**
 * Submit a bridge deposit (EIP-3009 gasless deposit to BridgePool)
 */
export async function submitBridgeDeposit(params: {
  from: string;
  amount: string;
  validAfter: number;
  validBefore: number;
  nonce: string;
  signature: string;
}): Promise<{ txId: string; status: string; transactionHash?: string }> {
  const response = await fetch(`${SWAP_API_URL}/bridge/deposit`, {
    signal: AbortSignal.timeout(10000),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const err = (await response.json().catch(() => ({ error: 'Unknown error' }))) as {
      error?: string;
    };
    throw new Error(err.error || response.statusText);
  }

  return response.json() as Promise<{ txId: string; status: string; transactionHash?: string }>;
}

/**
 * Get bridge deposit tx status
 */
export async function getBridgeDepositStatus(txId: string): Promise<SwapResponse | null> {
  const response = await fetch(`${SWAP_API_URL}/bridge/status/${txId}`, {
    signal: AbortSignal.timeout(10000),
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    const err = (await response.json().catch(() => ({ error: response.statusText }))) as {
      error?: string;
    };
    throw new Error(err.error || `Bridge status check failed (${response.status})`);
  }
  return response.json() as Promise<SwapResponse>;
}

// ── Bridge types ──

export interface BridgeDepositResult {
  txId: string;
  status: string;
  transactionHash: string;
}

export interface BridgeTransferStatus {
  depositTxHash: string;
  status: 'pending' | 'confirming' | 'releasing' | 'completed' | 'failed' | 'orphaned';
  sourceChain: string;
  destinationChain: string;
  amount: string;
  releaseTxHash: string | null;
  error: string | null;
}

/**
 * Submit EIP-2612 permit deposit (Polygon USDT -> Base USDC)
 */
export async function submitBridgeDepositPermit(params: {
  owner: string;
  amount: string;
  deadline: number;
  v: number;
  r: string;
  s: string;
}): Promise<BridgeDepositResult> {
  const response = await fetch(`${SWAP_API_URL}/bridge/deposit-permit`, {
    signal: AbortSignal.timeout(10000),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({ error: 'Unknown error' }))) as {
      error?: string;
    };
    throw new Error(err.error || 'Failed to submit bridge deposit');
  }
  return response.json() as Promise<BridgeDepositResult>;
}

/**
 * Query bridge transfer status (cross-chain release tracking)
 */
export async function getBridgeTransferStatus(
  depositTxHash: string
): Promise<BridgeTransferStatus> {
  const response = await fetch(`${SWAP_API_URL}/bridge/transfer/${depositTxHash}`, {
    signal: AbortSignal.timeout(10000),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({ error: 'Unknown error' }))) as {
      error?: string;
    };
    throw new Error(err.error || 'Failed to fetch bridge transfer status');
  }
  return response.json() as Promise<BridgeTransferStatus>;
}

/**
 * @spec docs/specs/features/cashout-bridge-timing-fix.md
 * Get the current EIP-2612 permit nonce for Polygon USDT.
 * Returns the nonce and the relay wallet address (the spender).
 */
export async function getPermitNonce(address: string): Promise<{ nonce: string; spender: string }> {
  const response = await fetch(`${SWAP_API_URL}/bridge/permit-nonce/${address}`);

  if (!response.ok) {
    const err = (await response.json().catch(() => ({ error: 'Unknown error' }))) as {
      error?: string;
    };
    throw new Error(err.error || response.statusText);
  }

  return response.json() as Promise<{ address: string; nonce: string; spender: string }>;
}

/**
 * @spec docs/specs/features/cashout-bridge-timing-fix.md
 * Submit a gasless USDT transfer on Polygon via permit + transferFrom.
 * Used for Step 3 of the Privy cash-out flow.
 */
export async function submitBridgeTransfer(params: {
  from: string;
  to: string;
  amount: string;
  deadline: number;
  v: number;
  r: string;
  s: string;
  flowId?: string;
}): Promise<{ permitTxHash: string; transferTxHash: string; status: string }> {
  const response = await fetch(`${SWAP_API_URL}/bridge/transfer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const err = (await response.json().catch(() => ({ error: 'Unknown error' }))) as {
      error?: string;
    };
    throw new Error(err.error || response.statusText);
  }

  return response.json() as Promise<{
    permitTxHash: string;
    transferTxHash: string;
    status: string;
  }>;
}
